import type { KidMarker } from '../../../../../../../api/kids/types';
import {
  COMPOSITION_OF_COSTS_MARKERS,
  COSTS_OVER_TIME_MARKERS,
  PERFORMANCE_SCENARIO_MARKERS,
  SRI_MARKER,
} from './markerMappings';
import {
  buildMarkerLookup,
  formatCurrencyValue,
  formatPercentPerYearValue,
  formatPercentValue,
  getMarkerValue,
  normalizeSriValue,
} from './markerUtils';

const COST_ROW_DESCRIPTIONS: Record<string, string> = {
  entry_cost: 'The impact of the costs you pay when entering your investment.',
  exit_cost:
    'The maximum impact of the costs of exiting your investment when it matures.',
  transaction_cost:
    'The impact of the costs of us buying and selling underlying investments for the product.',
  ongoing_cost:
    'The impact of the costs that we take each year for managing your investments.',
  performance_fee:
    'The impact of the performance fee. We take these from your investment if the product outperforms its high water mark, of (a) the highest previous net asset value per share of the series concerned at the end of a performance period; and (b) the initial issue price of the shares of that series.',
};

function formatFigValue(
  lookup: ReturnType<typeof buildMarkerLookup>,
  placeholder: string,
): string {
  const raw = getMarkerValue(lookup, 'FIG', placeholder);
  if (raw == null || raw === '') return '';

  if (placeholder.startsWith('riy_')) {
    return formatPercentPerYearValue(raw);
  }
  if (
    placeholder.includes('amount_back') ||
    placeholder.startsWith('total_cost_')
  ) {
    const currency = getMarkerValue(lookup, 'FUND', 'fund_currency');
    return formatCurrencyValue(raw, currency);
  }
  if (placeholder.includes('return_back')) {
    return formatPercentValue(raw);
  }
  return raw;
}

/** Build composition of costs table rows from COST markers. */
export function buildCompositionOfCostsTableFromMarkers(
  markers: KidMarker[],
): string[][] {
  const lookup = buildMarkerLookup(markers);

  const header: string[][] = [
    ['This table shows the impact on return per year', '', '', ''],
  ];

  const rows = COMPOSITION_OF_COSTS_MARKERS.map((row) => {
    const raw = getMarkerValue(lookup, row.markerType, row.placeholder);
    return [
      row.category,
      row.costType,
      raw != null && raw !== '' ? formatPercentValue(raw) : '',
      COST_ROW_DESCRIPTIONS[row.placeholder] ?? '',
    ];
  });

  return header.concat(rows);
}

/** Build costs over time table rows from FIG + FUND markers. */
export function buildCostsOverTimeTableFromMarkers(
  markers: KidMarker[],
): string[][] {
  const lookup = buildMarkerLookup(markers);
  const currency = getMarkerValue(lookup, 'FUND', 'fund_currency');
  const holdingPeriod = getMarkerValue(lookup, 'FUND', 'holding_period') ?? '3';
  const {
    totalCostOneYear,
    totalCostHoldingPeriod,
    riyOneYear,
    riyHoldingPeriod,
  } = COSTS_OVER_TIME_MARKERS;

  const investmentLabel = currency
    ? `Investment Scenarios ${currency} 10,000`
    : 'Investment Scenarios 10,000';

  const header: string[][] = [
    [
      investmentLabel,
      'If you cash in after 1 year',
      `If you exit after ${holdingPeriod} years`,
    ],
  ];

  const rows: string[][] = [
    [
      'Total costs',
      formatFigValue(lookup, totalCostOneYear.placeholder),
      formatFigValue(lookup, totalCostHoldingPeriod.placeholder),
    ],
    [
      'Reduction in Yield (RIY) per year*',
      formatFigValue(lookup, riyOneYear.placeholder),
      formatFigValue(lookup, riyHoldingPeriod.placeholder),
    ],
  ];

  return header.concat(rows);
}

/** Build performance scenarios table rows from FIG + FUND markers. */
export function buildPerformanceScenariosTableFromMarkers(
  markers: KidMarker[],
): string[][] {
  const lookup = buildMarkerLookup(markers);
  const holdingPeriod = getMarkerValue(lookup, 'FUND', 'holding_period') ?? '3';

  const header: string[][] = [
    [
      'Scenarios',
      '',
      '1 Year',
      `${holdingPeriod} years (Recommended Holding Period)`,
    ],
  ];

  const rows = PERFORMANCE_SCENARIO_MARKERS.flatMap((scenario) => [
    [
      scenario.label,
      'What you might get back after costs',
      formatFigValue(lookup, scenario.amountOneYear),
      formatFigValue(lookup, scenario.amountHolding),
    ],
    [
      '',
      'Average return each year',
      formatFigValue(lookup, scenario.returnOneYear),
      formatFigValue(lookup, scenario.returnHolding),
    ],
  ]);

  return header.concat(rows);
}

/** Build SRI numeric content from the FIG:sri marker. */
export function buildSriValueFromMarkers(markers: KidMarker[]): number | null {
  const lookup = buildMarkerLookup(markers);
  const raw = getMarkerValue(
    lookup,
    SRI_MARKER.markerType,
    SRI_MARKER.placeholder,
  );
  if (raw == null || raw === '') return null;
  return normalizeSriValue(raw);
}
